import { useEffect, useState } from 'react'
import { Button, Drawer, Icon, IconButton, Money, SearchSelect, confirmDialog } from '../components'
import { useLicense, useUpdateLicense, useUnlinkPayment } from '../lib/api/licenses'
import { useLicenseTariffs } from '../lib/api/license-tariffs'
import { fmtMoney } from '../lib/money'
import { LicensePaymentDrawer } from './license-payment-drawer'

export function LicenseDetailDrawer({
  licenseId,
  canEdit,
  canDelete,
  onClose,
}: {
  licenseId: string
  canEdit: boolean
  canDelete: boolean
  onClose: () => void
}) {
  const detail = useLicense(licenseId)
  const unlink = useUnlinkPayment()
  const tariffs = useLicenseTariffs()
  const updateLicense = useUpdateLicense()
  const [payVendorOpen, setPayVendorOpen] = useState(false)

  if (detail.isLoading) {
    return (
      <Drawer onClose={onClose} title="Ліцензія" width={640}>
        <div style={{ padding: 16, color: 'var(--fg-3)' }}>Завантаження…</div>
      </Drawer>
    )
  }
  if (detail.isError || !detail.data) {
    return (
      <Drawer onClose={onClose} title="Ліцензія" width={640}>
        <div style={{ padding: 16, color: 'var(--danger-fg)' }}>
          Ліцензія не існує або була видалена.
        </div>
      </Drawer>
    )
  }

  // Vendor side only — a license syncs in after the client invoice is fully paid,
  // so we don't track the sale (incoming) side here.
  const { license, tariff, payments, costEffective } = detail.data
  const amountPaidOut = payments
    .filter((p) => p.direction === 'out')
    .reduce((s, p) => s + p.amount, 0)
  // What we owe is the tariff price minus this license's vendor discount.
  const cost = costEffective
  const outstandingOut = cost != null ? Math.max(0, cost - amountPaidOut) : 0
  const portalUrl = import.meta.env.VITE_B24_PORTAL_URL ?? ''

  return (
    <>
      <Drawer onClose={onClose} title={license.title} width={640}>
        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ fontSize: 13, color: 'var(--fg-3)' }}>
            ID: <span style={{ fontFamily: 'var(--font-mono)' }}>{license.bitrixLicenseId}</span>{' '}
            · валідна до {license.validTo ?? '—'}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
            <KpiBlock
              label="Сплачено вендору"
              value={
                tariff && cost != null
                  ? `${fmtMoney(amountPaidOut, tariff.costCurrency)} / ${fmtMoney(cost, tariff.costCurrency)}`
                  : '—'
              }
              pct={
                cost != null && cost > 0
                  ? Math.min(100, Math.round((amountPaidOut / cost) * 100))
                  : 0
              }
            />
            <KpiBlock
              label="Борг перед вендором"
              value={tariff ? fmtMoney(outstandingOut, tariff.costCurrency) : '—'}
              tone={outstandingOut > 0 ? 'warning' : undefined}
            />
          </div>

          <div className="card" style={{ padding: 12 }}>
            <div className="kpi-eyebrow" style={{ marginBottom: 6 }}>Тариф</div>
            {tariff ? (
              <div style={{ fontSize: 13, color: 'var(--fg-1)' }}>
                <span style={{ color: 'var(--fg-3)' }}>вендор: {tariff.vendorName}</span> ·{' '}
                {license.discountPercent > 0 ? (
                  <>
                    <span
                      className="num"
                      style={{ textDecoration: 'line-through', color: 'var(--fg-3)' }}
                    >
                      {fmtMoney(tariff.costPrice, tariff.costCurrency)}
                    </span>{' '}
                    <span className="num">
                      −{license.discountPercent}% ={' '}
                      {fmtMoney(cost ?? tariff.costPrice, tariff.costCurrency)}
                    </span>
                  </>
                ) : (
                  <span className="num">{fmtMoney(tariff.costPrice, tariff.costCurrency)}</span>
                )}
                {license.tariffManual && (
                  <span
                    style={{
                      marginLeft: 8,
                      fontSize: 11,
                      padding: '1px 6px',
                      borderRadius: 4,
                      background: 'var(--bg-4)',
                      color: 'var(--fg-3)',
                    }}
                    title="Тариф зіставлено вручну — синхронізація його не перезапише"
                  >
                    вручну
                  </span>
                )}
              </div>
            ) : (
              <div style={{ fontSize: 13, color: 'var(--warning-fg)' }}>
                <Icon name="alert-triangle" size={12} /> Тариф не зіставлено.
                {canEdit && (
                  <span style={{ color: 'var(--fg-3)' }}>
                    {' '}Зіставте вручну нижче або створіть тариф у Налаштуваннях → Тарифи ліцензій.
                  </span>
                )}
              </div>
            )}
            {canEdit && (
              <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
                <SearchSelect
                  className="input"
                  style={{ flex: 1 }}
                  value={license.tariffId ?? ''}
                  disabled={updateLicense.isPending || tariffs.isLoading}
                  onChange={(id) => updateLicense.mutate({ licenseId, tariffId: id || null })}
                  emptyOption
                  emptyLabel="— не зіставлено (авто із синхронізації)"
                  placeholder="— не зіставлено (авто із синхронізації)"
                  options={(tariffs.data ?? []).map((t) => ({
                    id: t.id,
                    label: t.name,
                    hint: fmtMoney(t.costPrice, t.costCurrency),
                  }))}
                />
                {updateLicense.isPending && (
                  <span style={{ fontSize: 12, color: 'var(--fg-3)' }}>Збереження…</span>
                )}
              </div>
            )}
            {canEdit && (
              <DiscountField
                licenseId={licenseId}
                value={license.discountPercent}
                listPrice={tariff?.costPrice ?? null}
                currency={tariff?.costCurrency ?? null}
              />
            )}
          </div>

          <div className="card">
            <div className="card-head">
              <h3>Платежі</h3>
              <span style={{ color: 'var(--fg-3)', fontSize: 11 }}>{payments.length} рухів</span>
            </div>
            <table className="tbl">
              <thead>
                <tr>
                  <th style={{ width: 110 }}>Дата</th>
                  <th style={{ width: 110 }}>Тип</th>
                  <th>Транзакція</th>
                  <th className="num" style={{ width: 140 }}>Сума</th>
                  {canDelete && <th style={{ width: 50 }}></th>}
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id}>
                    <td className="fg2">{p.createdAt.slice(0, 10)}</td>
                    <td className="fg3">
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <Icon
                          name={p.direction === 'in' ? 'arrow-down-right' : 'arrow-up-right'}
                          size={12}
                          color={p.direction === 'in' ? 'var(--success-fg)' : 'var(--fg-4)'}
                        />
                        {p.direction === 'in' ? 'Прихід' : 'Витрата'}
                      </span>
                    </td>
                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                      {p.transactionId.slice(0, 12)}…
                    </td>
                    <td className="num"><Money amount={p.amount} currency={p.currency} /></td>
                    {canDelete && (
                      <td>
                        <IconButton
                          icon="trash-2"
                          tooltip="Видалити прив'язку"
                          onClick={async () => {
                            const ok = await confirmDialog({
                              title: "Видалити прив'язку?",
                              body: "Транзакція залишиться. Видалиться лише зв'язок із ліцензією.",
                              tone: 'danger',
                            })
                            if (ok) unlink.mutate({ licenseId, paymentId: p.id })
                          }}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            {payments.length === 0 && (
              <div className="fg3" style={{ padding: 16, textAlign: 'center' }}>
                Платежів ще немає.
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: 8 }}>
              {portalUrl && (
                <Button
                  variant="ghost"
                  size="sm"
                  icon="external-link"
                  onClick={() =>
                    window.open(
                      `${portalUrl}/crm/type/161/details/${license.bitrixLicenseId}/`,
                      '_blank',
                      'noopener,noreferrer',
                    )
                  }
                >
                  Відкрити в B24
                </Button>
              )}
              <Button
                variant="primary"
                size="sm"
                icon="credit-card"
                disabled={tariff == null}
                onClick={() => setPayVendorOpen(true)}
                title={tariff == null ? 'Спершу зіставте тариф' : undefined}
              >
                Оплатити вендору
              </Button>
            </div>
          </div>
        </div>
      </Drawer>

      {payVendorOpen && (
        <LicensePaymentDrawer detail={detail.data} onClose={() => setPayVendorOpen(false)} />
      )}
    </>
  )
}

// Vendor promo on this one license. B24 hides it inside the deal product-row
// name (DISCOUNT_RATE is always 0 there), so it can't be synced — it is typed
// here and cuts only what we owe the vendor, never the client's sale price.
function DiscountField({
  licenseId,
  value,
  listPrice,
  currency,
}: {
  licenseId: string
  value: number
  listPrice: number | null
  currency: 'UAH' | 'USD' | 'EUR' | null
}) {
  const update = useUpdateLicense()
  const [draft, setDraft] = useState(String(value))
  // Re-sync when the server value changes (another edit, a refetch).
  useEffect(() => setDraft(String(value)), [value])

  const parsed = Number(draft.replace(',', '.'))
  const valid = draft.trim() !== '' && Number.isFinite(parsed) && parsed >= 0 && parsed <= 100
  const save = () => {
    if (!valid || parsed === value) return
    update.mutate({ licenseId, discountPercent: parsed })
  }

  return (
    <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <label style={{ fontSize: 12, color: 'var(--fg-3)' }} htmlFor={`disc-${licenseId}`}>
        Знижка вендора, %
      </label>
      <input
        id={`disc-${licenseId}`}
        className="input"
        style={{ width: 90, textAlign: 'right' }}
        inputMode="decimal"
        value={draft}
        disabled={update.isPending}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur()
          if (e.key === 'Escape') setDraft(String(value))
        }}
      />
      {!valid ? (
        <span style={{ fontSize: 12, color: 'var(--danger-fg)' }}>від 0 до 100</span>
      ) : update.isPending ? (
        <span style={{ fontSize: 12, color: 'var(--fg-3)' }}>Збереження…</span>
      ) : listPrice != null && currency != null && parsed > 0 ? (
        <span style={{ fontSize: 12, color: 'var(--fg-3)' }}>
          платимо вендору {fmtMoney(Math.round(listPrice * (1 - parsed / 100) * 100) / 100, currency)}
        </span>
      ) : (
        <span style={{ fontSize: 12, color: 'var(--fg-3)' }}>
          ціна вендора без знижки
        </span>
      )}
    </div>
  )
}

function KpiBlock({
  label,
  value,
  pct,
  tone,
}: {
  label: string
  value: string
  pct?: number
  tone?: 'warning'
}) {
  return (
    <div
      style={{
        padding: '12px 14px',
        background: 'var(--bg-3)',
        border: '1px solid var(--border-default)',
        borderRadius: 6,
      }}
    >
      <div className="kpi-eyebrow" style={{ marginBottom: 4 }}>{label}</div>
      <div
        style={{
          fontSize: 18,
          fontWeight: 600,
          color: tone === 'warning' ? 'var(--warning-fg)' : 'var(--fg-1)',
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: '-0.02em',
        }}
      >
        {value}
      </div>
      {pct != null && (
        <div
          style={{
            marginTop: 6,
            height: 4,
            background: 'var(--bg-4)',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${pct}%`,
              background: pct >= 100 ? 'var(--success-fg)' : 'var(--accent)',
            }}
          />
        </div>
      )}
    </div>
  )
}
